//ActivityEditor.tsx
import React, { useState } from "react";
import {
  DndContext,
  closestCenter,
  useSensor,
  useSensors,
  PointerSensor,
} from "@dnd-kit/core";
import {
  useSortable,
  SortableContext,
  verticalListSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormHelperText,
  Typography,
  IconButton,
  Paper,
  TextField,
  Select,
  MenuItem,
  FormControl,
  Autocomplete,
  Grid,
  Collapse,
  Tooltip,
  InputLabel,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import ErrorIcon from "@mui/icons-material/Error";
import MailIcon from "@mui/icons-material/Mail";
import {
  appActivity,
  Approver,
  Reporter,
  ApprovalType,
  RejectionType,
  PercentageDetails,
  UserDetails,
  templateData,
  MailTemplateData,
  ToUser,
  MailRecipientTargets,
} from "../../../data/WorkFlowEditorType";
import { useNavigate } from "react-router-dom";
import EditMailFormatDialog from "./EditMailFormatDialog";

interface Props {
  activities: appActivity[];
  setActivities: (updated: appActivity[]) => void;
  templateList: templateData[];
  mailTemplateList: MailTemplateData[];
  approverList: Approver[];
  selectReporterList: Reporter[];
  selectToUserList: ToUser[];
  approvalTypeOptions: { value: ApprovalType; label: string }[];
  rejectionTypeOptions: { value: RejectionType; label: string }[];
  handleChangeName: (id: string, value: string) => void;
  handleChangeTemplate: (id: string, value: string) => void;
  handleChangeMailTemplate: (id: string, value: string) => void;
  handleChangeMailFormat: (
    id: string,
    subject: string,
    body: string,
    toUsers: ToUser[]
  ) => void;
  handleChangeReporter: (id: string, reporterIds: string[]) => void;
  handleChangeApprovalCondition: (id: string, value: string) => void;
  handleChangeRejectionCondition: (id: string, value: string) => void;
  handleChangePercentage: (id: string, percent: number, isApproval: boolean) => void;
  handleChangeConditionalApprover: (id: string, approverIds: string[], isApproval: boolean) => void;
  handleDelete: (id: string) => void;
  handleChangeMailRecipientTargets: (id: string, targets: MailRecipientTargets) => void;
  validationErrors?: ActivityValidationErrors;
  emptyError?: boolean;
  clearFieldError?: (fieldPath: string) => void;
}

// アクティビティごとのバリデーションエラー構造（key: applicationActivityId）
interface ActivityValidationErrors {
  [key: string]: {
    name?: string;
    template?: string;
    mailSetting?: string;
  };
}

/** 展開できるアクティビティ設定カード */
const ExpandableActivityCard: React.FC<{
  activity: appActivity;
  order: number;
  props: Props;
}> = ({ activity, order, props }) => {
  const navigate = useNavigate();
  const activityErrors =
    props.validationErrors?.[activity.applicationActivityId];
  const [expanded, setExpanded] = useState(false);
  const [openMailFormatDialog, setOpenMailFormatDialog] = useState(false);
  const [openLeaveConfirmDialog, setOpenLeaveConfirmDialog] = useState(false);
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: activity.applicationActivityId });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    marginBottom: 6,
    border: "1px solid #ccc",
    borderRadius: "6px",
    backgroundColor: "#f9f9f9",
    padding: 6,
    opacity: isDragging ? 0.9 : 1,
  };

  const details = activity.defaultConfig?.approval?.details;
  const approvalUserList =
    details && "userList" in details ? (details as UserDetails).userList : [];

  const rejectionDetails = activity.defaultConfig?.rejection?.details;
  const rejectionUserList =
    rejectionDetails && "userList" in rejectionDetails
      ? (rejectionDetails as UserDetails).userList
      : [];

  //エラーチェック
  const hasErrors = !!(activityErrors?.name || activityErrors?.template);

  return (
    <Box
      ref={setNodeRef}
      style={{
        ...style,
        border: hasErrors ? "2px solid #d32f2f" : "1px solid #ccc"
      }}
      sx={{ position: "relative" }}
    >
      {/* ドラッグheader */}
      <Box display="flex" alignItems="center">
        <Box
          {...attributes}
          {...listeners}
          sx={{
            cursor: "grab",
            mr: 1,
            px: 1,
            py: 0.5,
            borderRadius: "7px",
            border: "1px solid transparent",
            backgroundColor: "#f0f0f0",
            display: "flex",
            alignItems: "center",
            "&:hover": {
              border: "1px solid #d0d0d0",
              backgroundColor: "#d0d0d0",
            },
          }}
        >
          <DragIndicatorIcon fontSize="small" sx={{ color: "#888" }} />
        </Box>
        {/* 番号とテキスト部分 */}
        <Box display="flex" alignItems="center" flexGrow={1} ml={1}>
          {/* 丸い番号 */}
          <Box
            sx={{
              width: 24,
              height: 24,
              borderRadius: "50%",
              backgroundColor: "primary.main",
              color: "white",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "0.75rem",
              mr: 1,
            }}
          >
            {order}
          </Box>
          {/* テキスト */}
          <Typography variant="body1">
            {activity.appActivityName || "（名称未設定）"}
          </Typography>

          {/* エラーアイコン */}
          {hasErrors && (
            <ErrorIcon sx={{ color: "#d32f2f", ml: 1 }} />
          )}
        </Box>

        <IconButton onClick={() => setExpanded((prev) => !prev)} sx={{}}>
          {expanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
        </IconButton>
        <IconButton onClick={() => props.handleDelete(activity.applicationActivityId)}>
          <DeleteIcon color="error" />
        </IconButton>
      </Box>

      {/* 展開エリア */}
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <Box mt={2} position="relative">
          <TextField
            label="アクティビティ名"
            fullWidth
            value={activity.appActivityName}
            error={!!activityErrors?.name}
            helperText={activityErrors?.name}
            onChange={(e) =>
              props.handleChangeName(activity.applicationActivityId, e.target.value)
            }
            sx={{
              mb: 2,
              '& .MuiFormHelperText-root': {
                fontSize: '1rem'
              }
            }}
          />
          <FormControl fullWidth sx={{ mb: 2 }} error={!!activityErrors?.template}>
            <InputLabel>テンプレート</InputLabel>
            <Select
              value={activity.template?.templateName || ""}
              onChange={(e) =>
                props.handleChangeTemplate(activity.applicationActivityId, e.target.value)
              }
              label="テンプレート"
              error={!!activityErrors?.template}
            >
              {props.templateList.map((t) => (
                <MenuItem key={t.id} value={t.templateName}>
                  {t.templateName}
                </MenuItem>
              ))}
            </Select>
            {activityErrors?.template && (
              <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75, fontSize: '1rem' }}>
                {activityErrors.template}
              </Typography>
            )}
          </FormControl>

          {/*照査アクティビティの際（template.code === "1" ）の条件設定 */}
          {activity.template.code === "1" && (
            <Grid container spacing={2}>
              <Grid size={6}>
                <Typography variant="subtitle2">承認条件：</Typography>
                <Select
                  fullWidth
                  value={activity.defaultConfig?.approval?.type || ""}
                  onChange={(e) =>
                    props.handleChangeApprovalCondition(activity.applicationActivityId, e.target.value)
                  }
                  sx={{ mb: 2 }}
                >
                  {props.approvalTypeOptions.map(({ value, label }) => (
                    <MenuItem key={value} value={value}>
                      {label}
                    </MenuItem>
                  ))}
                </Select>
                {/*  （条件付き承認）バックエンド未実装のため一時的に非表示
                {activity.defaultConfig?.approval?.type === "CONDITIONAL_APPROVED" && (
                  <Autocomplete
                    multiple
                    options={props.approverList}
                    getOptionLabel={(option) => `${option.lastName} ${option.firstName}`}
                    value={props.approverList.filter((a) =>
                      approvalUserList.some((u) => u.id === a.id)
                    )}
                    onChange={(event, newValue) =>
                      props.handleChangeConditionalApprover(
                        activity.applicationActivityId,
                        newValue.map((a) => a.id),
                        true
                      )
                    }
                    renderInput={(params) => (
                      <TextField {...params} label="照査者" placeholder="照査者指定" />
                    )}
                    sx={{ mb: 2 }}
                  />
                )} */}

                {activity.defaultConfig?.approval?.type === "PERCENTAGE_APPROVED" && (
                  <TextField
                    label="承認必要割合（%）"
                    type="number"
                    fullWidth
                    value={(activity.defaultConfig.approval.details as PercentageDetails)?.requiredPercent || ""}
                    onChange={(e) =>
                      props.handleChangePercentage(activity.applicationActivityId, Number(e.target.value), true)
                    }
                    sx={{ mb: 2 }}
                  />
                )}
              </Grid>

              <Grid size={6}>
                <Typography variant="subtitle2">却下条件：</Typography>
                <Select
                  fullWidth
                  value={activity.defaultConfig?.rejection?.type || ""}
                  onChange={(e) =>
                    props.handleChangeRejectionCondition(activity.applicationActivityId, e.target.value)
                  }
                  sx={{ mb: 2 }}
                >
                  {props.rejectionTypeOptions.map(({ value, label }) => (
                    <MenuItem key={value} value={value}>
                      {label}
                    </MenuItem>
                  ))}
                </Select>

                {/*  （条件付き却下）バックエンド未実装のため一時的に非表示
                 {activity.defaultConfig?.rejection?.type === "CONDITIONAL_DENIED" && (
                  <Autocomplete
                    multiple
                    options={props.approverList}
                    getOptionLabel={(option) => `${option.lastName} ${option.firstName}`}
                    value={props.approverList.filter((a) =>
                      rejectionUserList.some((u) => u.id === a.id)
                    )}
                    onChange={(event, newValue) =>
                      props.handleChangeConditionalApprover(
                        activity.applicationActivityId,
                        newValue.map((a) => a.id),
                        false
                      )
                    }
                    renderInput={(params) => (
                      <TextField {...params} label="照査者" placeholder="照査者指定" />
                    )}
                    sx={{ mb: 2 }}
                  />
                )} */}

                {activity.defaultConfig?.rejection?.type === "PERCENTAGE_DENIED" && (
                  <TextField
                    label="却下必要割合（%）"
                    type="number"
                    fullWidth
                    value={(activity.defaultConfig.rejection.details as PercentageDetails)?.requiredPercent || ""}
                    onChange={(e) =>
                      props.handleChangePercentage(activity.applicationActivityId, Number(e.target.value), false)
                    }
                    sx={{ mb: 2 }}
                  />
                )}
              </Grid>
            </Grid>
          )}

          {/* メール送信アクティビティ際（template.code === "2"）のテンプレート・フォーマット設定*/}
          {activity.template.code === "2" && (
            <Box sx={{ mb: 0.5 }}>
              <Button
                startIcon={<MailIcon />}
                onClick={() => setOpenMailFormatDialog(true)}
                variant="outlined"
                color={activityErrors?.mailSetting ? "error" : "primary"}
              >
                メールフォーマット設定
              </Button>
              <FormHelperText error sx={{ ml: 1.75, fontSize: "1rem" }}>
                {activityErrors?.mailSetting || ""}
              </FormHelperText>
            </Box>
          )}

          {/* 応答アクティビティ際（template.code === "4"）の報告者設定*/}
          {activity.template.code === "4" && (
            <Autocomplete
              multiple
              options={props.selectReporterList}
              getOptionLabel={(option) => `${option.lastName} ${option.firstName}`}
              value={props.selectReporterList.filter((r) =>
                activity.reporterList?.some((u) => u.id === r.id)
              )}
              onChange={(event, newValue) =>
                props.handleChangeReporter(
                  activity.applicationActivityId,
                  newValue.map((r) => r.id)
                )
              }
              renderInput={(params) => (
                <TextField {...params} label="報告者" placeholder="報告者指定" />
              )}
              sx={{ mb: 2 }}
            />
          )}
        </Box>
      </Collapse>
      <EditMailFormatDialog
        open={openMailFormatDialog}
        onClose={() => setOpenMailFormatDialog(false)}
        handleEditMailTemplate={() => setOpenLeaveConfirmDialog(true)}
        mailTemplateList={props.mailTemplateList}
        selectToUserList={props.selectToUserList}
        activityId={activity.applicationActivityId}
        mailFormat={activity.mailFormat}
        handleChangeMailFormat={props.handleChangeMailFormat}
        defaultConfig={activity.defaultConfig}
        handleChangeMailRecipientTargets={props.handleChangeMailRecipientTargets}

      />
      <Dialog
        open={openLeaveConfirmDialog}
        onClose={() => setOpenLeaveConfirmDialog(false)}
      >
        <DialogTitle>ページ移動確認</DialogTitle>
        <DialogContent>
          <DialogContentText>
            編集内容は保存されていません。ページを離れてもよろしいですか？
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenLeaveConfirmDialog(false)}>戻る</Button>
          <Button
            onClick={() => {
              navigate("/MailTemplatePage");
              setOpenLeaveConfirmDialog(false);
            }}
            color="error"
          >
            離脱
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

/** アクティビティ編集エリア */
const ActivityEditor: React.FC<Props> = (props) => {
  const sensors = useSensors(useSensor(PointerSensor));
  //ドラッグ後の順番調整
  const handleDragEnd = (event: any) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      const oldIndex = props.activities.findIndex((item) => item.applicationActivityId === active.id);
      const newIndex = props.activities.findIndex((item) => item.applicationActivityId === over?.id);
      const reordered = arrayMove(props.activities, oldIndex, newIndex).map((act, idx) => ({
        ...act,
        order: idx + 1,
      }));
      props.setActivities(reordered);
    }
  };
  //アクティビティ追加
  const handleAddActivity = () => {
    const newId = crypto.randomUUID();
    const newOrder = props.activities.length + 1;
    const newActivity: appActivity = {
      applicationActivityId: newId,
      appActivityName: "",
      order: newOrder,
      template: { id: "", templateName: "", code: "" },
      defaultConfig: {},
      reporterList: [],
    };
    props.setActivities([...props.activities, newActivity]);
    // 空エラーをクリア
    if (props.clearFieldError && props.emptyError) {
      props.clearFieldError('activitiesEmpty');
    }
  };

  return (
    <Box
      border={props.emptyError ? 2 : 1}
      borderColor={props.emptyError ? "#d32f2f" : "grey.300"}
      borderRadius={1}
      p={2}
      sx={{ position: 'relative' }}
    >
      <Box sx={{ display: "flex", alignItems: "center" }}>
        <Typography variant="h3" fontWeight="bold" sx={{ color: "GrayText", mb: 0.5 }}>
          アクティビティ設定
        </Typography>
        <EditIcon sx={{ fontSize: 20, color: "GrayText" }} />
      </Box>
      {props.emptyError && (
        <Typography variant="body2" color="error" sx={{ mb: 1, fontSize: '1rem', display: 'flex', alignItems: 'center' }}>
          <ErrorIcon sx={{ fontSize: 16, mr: 0.5 }} />
          アクティビティを追加してください
        </Typography>
      )}
      <Paper sx={{ p: 2 }}>
        <Typography
          variant="body2"
          color="textSecondary"
          sx={{ mx: 1, display: "flex", alignItems: "center" }}
        >
          左側の <DragIndicatorIcon fontSize="small" sx={{ color: "#888" }} />
          アイコンをドラッグして項目の順番を変更できます
        </Typography>

        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext
            items={props.activities.map((a) => a.applicationActivityId)}
            strategy={verticalListSortingStrategy}
          >
            {props.activities
              .sort((a, b) => a.order - b.order)
              .map((activity, index) => (
                <ExpandableActivityCard
                  key={activity.applicationActivityId}
                  activity={activity}
                  order={index + 1}
                  props={props}
                />
              ))}
          </SortableContext>
        </DndContext>

        <Box mt={2} display="flex" justifyContent="center">
          <Tooltip title="追加">
            <IconButton onClick={handleAddActivity}>
              <AddIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Paper>
    </Box>
  );
};

export default ActivityEditor;
